import { formatDate } from './formatters';
const PRIMARY_RGB = [0, 128, 203];

const totalsBySoftware = (clients) => {
  const totals = new Map();
  clients.forEach((client) =>
    client.software.forEach((sw) => {
      const entry = totals.get(sw.software_id) || {
        software_name: sw.software_name,
        seats: 0,
      };
      entry.seats += sw.seats;
      totals.set(sw.software_id, entry);
    })
  );
  return [...totals.values()].sort((a, b) =>
    a.software_name.localeCompare(b.software_name, 'es')
  );
};

/** Agrupa los códigos de un software por día de alta, en orden cronológico. */
const groupCodesByIssuedDate = (codes) => {
  const groups = new Map();
  [...codes]
    .sort((a, b) => new Date(a.issued_date || 0) - new Date(b.issued_date || 0))
    .forEach((code) => {
      const date = code.issued_date ? formatDate(code.issued_date, 'short') : 'Sin fecha';
      const group = groups.get(date) || { date, codes: 0, seats: 0 };
      group.codes += 1;
      group.seats += code.seats;
      groups.set(date, group);
    });
  return [...groups.values()];
};

const fileDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Genera y descarga el PDF "Resumen de uso de licencias".
 * jspdf se carga bajo demanda para no sumarlo al bundle inicial ni al prerender SSR.
 */
export const downloadUsageReportPdf = async ({ clients, totals, scope, search }) => {
  const [{ jsPDF }, { autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const now = new Date();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...PRIMARY_RGB);
  doc.text('DICOMITY · Resumen de uso de licencias', marginX, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.text(`Generado: ${formatDate(now, 'datetime')}`, pageWidth - marginX, 16, {
    align: 'right',
  });

  const criteria = [
    scope === 'all' ? 'Todas las licencias' : 'Licencias activas y vigentes',
    search ? `Filtro: "${search}"` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  doc.text(`Criterio: ${criteria}`, marginX, 22);

  const softwareTotals = totalsBySoftware(clients);

  autoTable(doc, {
    startY: 27,
    theme: 'grid',
    head: [['Clientes', 'Softwares', 'Códigos de licencia', 'Licencias contratadas']],
    body: [[totals.clients, softwareTotals.length, totals.license_codes, totals.seats]],
    styles: { fontSize: 10, halign: 'center' },
    headStyles: { fillColor: PRIMARY_RGB, textColor: 255 },
    margin: { left: marginX, right: marginX },
  });

  const pageHeight = doc.internal.pageSize.getHeight();
  let cursorY = doc.lastAutoTable.finalY + 10;

  clients.forEach((client) => {
    const rowCount =
      client.software.reduce((n, sw) => n + groupCodesByIssuedDate(sw.codes).length + 1, 0) +
      (client.software.length > 1 ? 1 : 0);
    const estimatedHeight = 14 + (rowCount + 1) * 8.5;
    const usableHeight = pageHeight - 31;
    // Si el bloque del cliente entra completo en una página nueva, no se parte entre páginas.
    const doesNotFit = cursorY + estimatedHeight > pageHeight - 15;
    if ((doesNotFit && estimatedHeight <= usableHeight) || cursorY + 30 > pageHeight - 15) {
      doc.addPage();
      cursorY = 16;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(20);
    doc.text(client.client_name, marginX, cursorY);
    const nameWidth = doc.getTextWidth(client.client_name);
    if (client.company && client.company !== client.client_name) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(110);
      doc.text(client.company, marginX + nameWidth + 3, cursorY);
    }

    const rows = [];
    const softwareTotalRows = new Set();
    const clientTotalRows = new Set();

    client.software.forEach((sw) => {
      groupCodesByIssuedDate(sw.codes).forEach((group, index) => {
        rows.push([
          rows.length === 0 ? client.client_name : '',
          index === 0 ? sw.software_name : '',
          group.codes,
          group.seats,
          group.date,
        ]);
      });
      softwareTotalRows.add(rows.length);
      rows.push(['', `Total ${sw.software_name}`, sw.license_codes, sw.seats, '']);
    });

    if (client.software.length > 1) {
      clientTotalRows.add(rows.length);
      rows.push(['', 'Total cliente', client.license_codes, client.seats, '']);
    }

    autoTable(doc, {
      startY: cursorY + 3,
      theme: 'grid',
      head: [['Cliente', 'Software', 'Códigos de licencia', 'Licencias', 'Fecha de alta']],
      body: rows,
      styles: { fontSize: 9, cellPadding: 2, lineColor: [229, 231, 235] },
      headStyles: { fillColor: PRIMARY_RGB, textColor: 255 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 60 },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { cellWidth: 40 },
      },
      margin: { left: marginX, right: marginX },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (softwareTotalRows.has(data.row.index)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [243, 244, 246];
        } else if (clientTotalRows.has(data.row.index)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [219, 234, 254];
        }
      },
    });

    cursorY = doc.lastAutoTable.finalY + 10;
  });

  const totalGeneralHeight = 20 + (softwareTotals.length + 2) * 8.5;
  if (cursorY + totalGeneralHeight > pageHeight - 15) {
    doc.addPage();
    cursorY = 16;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(20);
  doc.text('Total general', marginX, cursorY);

  autoTable(doc, {
    startY: cursorY + 3,
    theme: 'grid',
    head: [['Software', 'Licencias contratadas']],
    body: softwareTotals.map((sw) => [sw.software_name, sw.seats]),
    foot: [['Total general', totals.seats]],
    tableWidth: 140,
    styles: { fontSize: 10, cellPadding: 2, lineColor: [229, 231, 235] },
    headStyles: { fillColor: PRIMARY_RGB, textColor: 255 },
    footStyles: { fillColor: [229, 231, 235], textColor: 20, fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'right' } },
    didParseCell: (data) => {
      if (data.section !== 'body' && data.column.index === 1) data.cell.styles.halign = 'right';
    },
    margin: { left: marginX, right: marginX },
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      'Licencias contratadas = instalaciones permitidas por contrato',
      marginX,
      doc.internal.pageSize.getHeight() - 8
    );
    doc.text(`Página ${page} de ${pageCount}`, pageWidth - marginX, doc.internal.pageSize.getHeight() - 8, {
      align: 'right',
    });
  }

  doc.save(`resumen-uso-licencias-${fileDate(now)}.pdf`);
};
